import React, { useState, useEffect, useRef, useMemo } from "react";
import { Field, Form, Formik, ErrorMessage } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import axios from "axios";
import { useSearchParams, useNavigate } from "react-router-dom";

const variables = [
    "cost",
    "consumption",
    "total_consumption",
    "surface_area",
    "total_surface",
    "ponderation",
    "total_key_ponderation",
];

const operators = ["*", "/", "+", "-", "(", ")"];

// Validation Schema
const getValidationSchema = () => Yup.object().shape({
    property_type: Yup.object().nullable().required("Building selection is required"),
    period: Yup.object().nullable().required("Period selection is required"),
    key_name: Yup.string().trim().required("Key name is required").min(2, "Key name must be at least 2 characters"),
    key_order: Yup.number()
        .transform((value, originalValue) => (originalValue === "" || originalValue === null ? null : value))
        .nullable()
        .integer("Key order must be an integer")
        .min(1, "Key order must be at least 1"),
    formula: Yup.string().trim().required("Formula is required").min(1, "Please build a formula using variables and operators"),
    // Costs are now optional to allow creating/updating keys without selecting any primary cost.
    // Validation for costs is intentionally relaxed here; backend will still safely handle empty costs.
    costs: Yup.array(),
    costFields: Yup.object().test(
        'cost-fields-validation',
        'Please select at least one field (ETC, DJF, or NJ) for all selected costs',
        function (value) {
            const { costs } = this.parent;
            if (!costs || costs.length === 0) return true;

            for (const cost of costs) {
                const fields = value?.[cost.value];
                const hasETC = fields?.etc && fields.etc.length > 0;
                const hasDJF = fields?.djf && fields.djf.length > 0;
                const hasNJ = fields?.nj && fields.nj.length > 0;

                if (!hasETC && !hasDJF && !hasNJ) {
                    return this.createError({
                        message: `Please select at least one field (ETC, DJF, or NJ) for cost: ${cost.label}`,
                        path: 'costFields'
                    });
                }
            }
            return true;
        }
    ),
    property_address: Yup.string().trim()
});

const presetFormulas = {
    consumption: "cost/total_consumption*(consumption*ponderation)",
    surface: "((surface_area*ponderation)/total_surface)*cost"
};

const CreateKey = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const editId = searchParams.get('edit');
    const duplicateId = searchParams.get('duplicate');
    const returnTo = searchParams.get('returnTo');
    const isEditing = !!editId;
    const isDuplicating = !!duplicateId;
    const backTarget = returnTo && returnTo.startsWith("/keys") ? returnTo : "/keys";

    // Dynamic data states
    const [buildings, setBuildings] = useState([]);
    const [periods, setPeriods] = useState([]);
    const [costData, setCostData] = useState([]);
    const [surfaceData, setSurfaceData] = useState([]);
    const [consumptionData, setConsumptionData] = useState([]);
    const [surfaceNames, setSurfaceNames] = useState([]); // For buyer dropdown
    const [objectData, setObjectData] = useState([]); // Objects for optional Object selector (surface destinations)
    const [existingKeys, setExistingKeys] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editData, setEditData] = useState(null);
    const [transferredCosts, setTransferredCosts] = useState([]);

    // Dynamic cost fields state
    const [costFields, setCostFields] = useState({});
    const formikRef = useRef(null);

    // ETC, DJF, NJ data states
    const [etcData, setEtcData] = useState([]);
    const [djfData, setDjfData] = useState([]);
    const [njData, setNjData] = useState([]);

    // Formula history for undo/redo
    const [formulaHistory, setFormulaHistory] = useState([""]);
    const [historyIndex, setHistoryIndex] = useState(0);

    // Multiple destinations
    const [destinations, setDestinations] = useState([{
        id: 1,
        access_level: "1", // "1"=Surface, "2"=Consumption, "3"=Object
        surface_area: null, // primary dropdown: surface ("1"), consumption ("2"), or surface ("3")
        team_name: "1",
        existing_key: "",
        buyer: null, // Buyer dropdown field
        object: null, // Object selection (Object flow + Consumption→Object)
        surface_sel: null, // Surface field for Consumption→Object (replaces Key)
        consumption_target: "buyer", // for access_level "2": "buyer" | "object"
        no_cost_destination: false,
        destination_order: ""
    }]);

    // Fetch buildings on component mount
    useEffect(() => {
        fetchBuildings();
    }, []);

    // Fetch edit data if editing or duplicating
    useEffect(() => {
        if (isEditing && editId) {
            fetchEditData(editId);
            fetchTransferredCosts(editId);
        } else if (isDuplicating && duplicateId) {
            fetchEditData(duplicateId, true); // Pass true to indicate duplicate mode
            // Don't fetch transferred costs for duplicate mode
        }
    }, [isEditing, editId, isDuplicating, duplicateId]);

    useEffect(() => {
        if (!isEditing) {
            setTransferredCosts([]);
        }
    }, [isEditing]);

    const validationSchema = useMemo(() => getValidationSchema(), []);

    // Handle edit data loading
    useEffect(() => {
        if (editData && buildings.length > 0) {
            // Set building and trigger period loading
            const buildingOption = buildings.find(b => b.value === editData.building_id);
            if (buildingOption && periods.length === 0) {
                // Load periods for this building
                handleBuildingChange(buildingOption, () => { });
            }

            // If periods are loaded and we have a period ID, load the data
            if (editData.building_time_period_id && periods.length > 0) {
                const periodOption = periods.find(p => p.value === editData.building_time_period_id);
                if (periodOption && costData.length === 0) {
                    handlePeriodChange(periodOption, buildingOption, () => { });
                }
            }
        }
    }, [editData, buildings, periods]);

    // Map destination surface areas after surface/consumption data loads
    useEffect(() => {
        if (editData && editData.destinations && (surfaceData.length > 0 || consumptionData.length > 0)) {
            const mappedDestinations = editData.destinations.map((dest, index) => {
                let properOption = null;

                // Find the matching option by destination_id
                if (dest.destination_type === "surface" && surfaceData.length > 0) {
                    // Support both:
                    // - New keys: destination_id stores the surface ID
                    // - Old keys: destination_id may store the numeric "value" field
                    properOption = surfaceData.find(surface => {
                        const destIdNum = parseFloat(dest.destination_id);
                        const surfaceIdNum = parseFloat(surface.id);
                        const surfaceValueNum = surface.original_value !== undefined
                            ? parseFloat(surface.original_value)
                            : parseFloat(surface.value);

                        return (
                            surface.id === dest.destination_id ||
                            surface.id === parseInt(dest.destination_id) ||
                            surface.value === dest.destination_id ||
                            surface.original_value === dest.destination_id ||
                            (!isNaN(destIdNum) && !isNaN(surfaceIdNum) && surfaceIdNum === destIdNum) ||
                            (!isNaN(destIdNum) && !isNaN(surfaceValueNum) && surfaceValueNum === destIdNum)
                        );
                    });
                } else if (dest.destination_type === "consumption" && consumptionData.length > 0) {
                    // Support both:
                    // - New keys: destination_id stores the consumption ID
                    // - Old keys: destination_id may store the numeric "value" field
                    properOption = consumptionData.find(consumption => {
                        const destIdNum = parseFloat(dest.destination_id);
                        const consumptionIdNum = parseFloat(consumption.id);
                        const consumptionValueNum = consumption.original_value !== undefined
                            ? parseFloat(consumption.original_value)
                            : parseFloat(consumption.value);

                        return (
                            consumption.id === dest.destination_id ||
                            consumption.id === parseInt(dest.destination_id) ||
                            consumption.value === dest.destination_id ||
                            consumption.original_value === dest.destination_id ||
                            (!isNaN(destIdNum) && !isNaN(consumptionIdNum) && consumptionIdNum === destIdNum) ||
                            (!isNaN(destIdNum) && !isNaN(consumptionValueNum) && consumptionValueNum === destIdNum)
                        );
                    });
                }

                // Create unique ID for React key (since API returns same ID for multiple destinations)
                const uniqueId = `${dest.id}_${index}`;

                // Find buyer option - surfaces use surfaceData, consumptions use surfaceNames
                let buyerOption = null;
                if (dest.destination_type === "surface" && dest.buyer_id && surfaceData.length > 0) {
                    buyerOption = surfaceData.find(item =>
                        item.value === dest.buyer_id ||
                        item.value === parseInt(dest.buyer_id)
                    );
                } else if (dest.destination_type === "consumption" && dest.buyer_id && surfaceNames.length > 0) {
                    buyerOption = surfaceNames.find(surface =>
                        surface.value === dest.buyer_id ||
                        surface.value === parseInt(dest.buyer_id)
                    );
                }

                // Object option — for the Object flow and Consumption→Object.
                let objectOption = null;
                if (dest.object_id && objectData.length > 0) {
                    objectOption = objectData.find(item =>
                        item.value === dest.object_id ||
                        item.value === parseInt(dest.object_id)
                    );
                }

                // Surface field option (surface_id) — Object flow + Consumption→Object.
                let surfaceSelOption = null;
                if (dest.surface_id && surfaceData.length > 0) {
                    surfaceSelOption = surfaceData.find(item =>
                        item.value === dest.surface_id ||
                        item.value === parseInt(dest.surface_id)
                    );
                }
                // Robustness: if the Surface link is missing/orphaned (e.g. surface_id was
                // dropped by an older duplication/import) but an object is selected, derive
                // the Surface from the object's parent surface — same as the create-form
                // auto-complete — so the Edit view always shows the correct Surface.
                if (!surfaceSelOption && objectOption && objectOption.object_surface != null && surfaceData.length > 0) {
                    surfaceSelOption = surfaceData.find(item =>
                        String(item.surface).trim() === String(objectOption.object_surface).trim()
                    );
                }

                const accessLevel = dest.destination_type === "object"
                    ? "3"
                    : (dest.destination_type === "consumption" ? "2" : "1");

                const mappedDest = {
                    id: uniqueId,
                    access_level: accessLevel, // ✅ "1"=surface, "2"=consumption, "3"=object
                    surface_area: properOption || null, // primary (surface/consumption); null for object flow
                    team_name: dest.ponderation?.toString() || "1",
                    existing_key: dest.destination_key_name || "",
                    buyer: buyerOption || null, // ✅ Map buyer data from buyer_id
                    object: objectOption || null, // ✅ Object flow + Consumption→Object
                    surface_sel: surfaceSelOption || null, // Surface field for object-routed flows
                    consumption_target: (dest.destination_type === "consumption" && dest.object_id) ? "object" : "buyer",
                    no_cost_destination: dest.no_cost_destination || false,
                    destination_order:
                        dest.destination_order !== null && dest.destination_order !== undefined
                            ? String(dest.destination_order)
                            : ""
                };

                return mappedDest;
            });
            setDestinations(mappedDestinations);
        }
    }, [editData, surfaceData, consumptionData, surfaceNames, objectData]);

    const fetchTransferredCosts = async (keyId) => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL}key/${keyId}/calculations`, {
                headers: {
                    "api-key": process.env.REACT_APP_API_KEY,
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
            });

            if (res.data.status === "success") {
                const calculations = res.data.data.calculations || [];
                const transferredOnly = calculations.filter(calc => calc?.is_transferred && !calc?.is_recalculated_transfer);

                const formatted = transferredOnly.map((calc, idx) => ({
                    id: calc.id || `transfer-${idx}`,
                    sourceKey: calc.transferred_from_key_name || "Unknown",
                    costName: calc.cost_name || calc.cost_number || "",
                    amountWithoutVat: calc.result_without_vat !== null && calc.result_without_vat !== undefined ? parseFloat(calc.result_without_vat) : null,
                    amountWithVat: calc.result_with_vat !== null && calc.result_with_vat !== undefined ? parseFloat(calc.result_with_vat) : null,
                    unit: calc.result_unit || "CHF",
                    costId: calc.transferred_cost_id || calc.cost_id || null,
                    sourceCostFields: calc.source_cost_fields || null
                }));

                setTransferredCosts(formatted);

                // Pre-fill ETC/DJF/NJ from source key for transferred costs
                // (only if not already saved on the receiving key)
                const currentFields = formikRef.current?.values?.costFields || costFields || {};
                const merged = { ...currentFields };
                let changed = false;
                for (const tc of formatted) {
                    if (tc.costId && !merged[String(tc.costId)] && tc.sourceCostFields) {
                        merged[String(tc.costId)] = tc.sourceCostFields;
                        changed = true;
                    }
                }
                if (changed) {
                    setCostFields(merged);
                    if (formikRef.current) {
                        formikRef.current.setFieldValue("costFields", merged);
                    }
                }
            } else {
                setTransferredCosts([]);
            }
        } catch (error) {
            console.error("Error fetching transferred costs:", error);
            setTransferredCosts([]);
        }
    };

    const fetchEditData = async (keyId, isDuplicate = false) => {
        try {
            setLoading(true);
            const idToFetch = keyId || editId || duplicateId;
            const res = await axios.get(`${process.env.REACT_APP_API_URL}key/${idToFetch}`, {
                headers: {
                    "api-key": process.env.REACT_APP_API_KEY,
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
            });
            if (res.data.status === "success") {
                const keyData = res.data.data.key;
                
                // For duplicate mode, clear key_name and costs
                const processedData = isDuplicate ? {
                    ...keyData,
                    key_name: "", // Clear key name for user to enter
                    costs: [], // Clear costs for user to select
                } : keyData;
                
                setEditData(processedData);

                // Set formula history for editing - reconstruct step by step
                const initialFormula = keyData.formula || "";
                if (initialFormula.trim()) {
                    // Parse the formula and reconstruct history step by step
                    const reconstructedHistory = reconstructFormulaHistory(initialFormula);
                    setFormulaHistory(reconstructedHistory);
                    setHistoryIndex(reconstructedHistory.length - 1);

                    // Debug logging in development mode
                    if (process.env.NODE_ENV === 'development') {
                        console.log("=== FORMULA HISTORY RECONSTRUCTED ===", {
                            originalFormula: initialFormula,
                            reconstructedHistory,
                            finalIndex: reconstructedHistory.length - 1
                        });
                    }
                } else {
                    // Empty formula - start fresh
                    setFormulaHistory([""]);
                    setHistoryIndex(0);
                }

                // Load cost fields data if available
                if (keyData.cost_fields_data) {
                    setCostFields(keyData.cost_fields_data);
                }

                // Destinations will be set by the useEffect after data loads
            }
        } catch (err) {
            console.error("Error fetching edit data:", err);
            alert("Error loading key data for editing");
        } finally {
            setLoading(false);
        }
    };

    const fetchBuildings = async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL}key/buildings`, {
                headers: {
                    "api-key": process.env.REACT_APP_API_KEY,
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
            });
            if (res.data.status === "success") {
                const buildingOptions = res.data.data.buildings.map(building => ({
                    value: building.id,
                    label: building.building_name,
                    time_periods: building.time_periods
                }));
                setBuildings(buildingOptions);
            }
        } catch (err) {
            console.error("Error fetching buildings:", err);
        }
    };

    const fetchData = async (buildingId, periodId) => {
        try {
            setLoading(true);
            const res = await axios.get(`${process.env.REACT_APP_API_URL}key/data/${buildingId}/${periodId}`, {
                headers: {
                    "api-key": process.env.REACT_APP_API_KEY,
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
            });

            // Fetch surface names for buyer dropdown
            const surfaceNamesRes = await axios.get(`${process.env.REACT_APP_API_URL}key/surface-names/${buildingId}/${periodId}`, {
                headers: {
                    "api-key": process.env.REACT_APP_API_KEY,
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
            });
            if (res.data.status === "success") {
                const { costs, surfaces, consumptions, objects, etc, djf, nj } = res.data.data;

                setCostData(costs.map(cost => ({
                    ...cost,
                    value: cost.id, // Use ID for selects
                    label: `${cost.cost} - ${cost.name} - ${cost.value}`
                })));

                // IMPORTANT:
                // - `value` MUST be the record ID for React-Select, otherwise
                //   multiple different surfaces/consumptions that share the same
                //   numeric "value" end up being treated as the same option.
                // - We keep the original numeric value in `original_value` so we
                //   can still correctly map older keys that stored destination_id
                //   using the numeric "value" instead of the ID.
                setSurfaceData(surfaces.map(surface => ({
                    ...surface,
                    original_value: surface.value,
                    value: surface.id,
                    label: `${surface.surface} - ${surface.name} - ${surface.value}`
                })));

                setConsumptionData(consumptions.map(consumption => ({
                    ...consumption,
                    original_value: consumption.value,
                    value: consumption.id,
                    label: `${consumption.consumption} - ${consumption.name} - ${consumption.value}`
                })));

                // Objects for the Object selector. `object_surface` lets us auto-fill the
                // Surface field. Label shows "number - tenant - surface value" (client
                // request), matching the Surface/Consumption dropdown style.
                setObjectData((objects || []).map(object => {
                    const number = object.objet_code || object.object || '';
                    const tenant = object.tenant?.name || '';
                    const surfaceValue = (object.object_surface_value !== null && object.object_surface_value !== undefined)
                        ? object.object_surface_value
                        : '';
                    return {
                        ...object,
                        value: object.id,
                        label: [number, tenant, surfaceValue]
                            .filter(part => part !== '' && part !== null && part !== undefined)
                            .join(' - ')
                    };
                }));

                // Set ETC, DJF, NJ data from their respective tables
                setEtcData(etc.map(item => ({
                    id: item.id,
                    value: item.value,
                    label: `${item.value}(${new Date(item.start_date).toLocaleDateString()}-${new Date(item.end_date).toLocaleDateString()})`,
                    start_date: item.start_date,
                    end_date: item.end_date
                })));

                setDjfData(djf.map(item => ({
                    id: item.id,
                    value: item.value,
                    label: `${item.value}(${new Date(item.start_date).toLocaleDateString()}-${new Date(item.end_date).toLocaleDateString()})`,
                    start_date: item.start_date,
                    end_date: item.end_date
                })));

                setNjData(nj.map(item => ({
                    id: item.id,
                    value: item.value,
                    label: `${item.value}(${new Date(item.start_date).toLocaleDateString()}-${new Date(item.end_date).toLocaleDateString()})`,
                    start_date: item.start_date,
                    end_date: item.end_date
                })));

                // Set surface names for buyer dropdown
                if (surfaceNamesRes.data.status === "success") {
                    setSurfaceNames(surfaceNamesRes.data.data.surface_names || []);
                }
            }
        } catch (err) {
            console.error("Error fetching data:", err);
        } finally {
            setLoading(false);
        }
    };

    const fetchExistingKeys = async (buildingId, periodId) => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL}key/existing`, {
                headers: {
                    "api-key": process.env.REACT_APP_API_KEY,
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
                params: { buildingId, periodId }
            });
            if (res.data.status === "success") {
                setExistingKeys(res.data.data.keys);
            }
        } catch (err) {
            console.error("Error fetching existing keys:", err);
        }
    };

    const handleBuildingChange = (building, setFieldValue) => {
        setFieldValue("property_type", building);
        setFieldValue("period", null);
        setPeriods(building ? building.time_periods.map(period => ({
            value: period.id,
            label: period.label
        })) : []);
        setCostData([]);
        setSurfaceData([]);
        setConsumptionData([]);
        setObjectData([]);
    };

    const handlePeriodChange = (period, building, setFieldValue) => {
        setFieldValue("period", period);
        if (building && period) {
            fetchData(building.value, period.value);
            fetchExistingKeys(building.value, period.value);
        }
    };

    // Handle cost selection changes and generate dynamic fields
    const handleCostChange = (selectedCosts, setFieldValue, currentCostFields) => {
        const newCostFields = {};

        // Preserve transferred cost field entries (keyed by cost IDs from transfers)
        const transferredCostIds = new Set(
            transferredCosts.filter(tc => tc.costId).map(tc => String(tc.costId))
        );
        for (const [key, val] of Object.entries(currentCostFields || {})) {
            if (transferredCostIds.has(key)) {
                newCostFields[key] = val;
            }
        }

        // Generate dynamic fields for each selected primary cost, preserving existing values
        (selectedCosts || []).forEach(cost => {
            if (currentCostFields && currentCostFields[cost.value]) {
                newCostFields[cost.value] = currentCostFields[cost.value];
            } else {
                newCostFields[cost.value] = {
                    etc: null,
                    djf: null,
                    nj: null
                };
            }
        });

        setFieldValue("costs", selectedCosts || []);
        setFieldValue("costFields", newCostFields);
        setCostFields(newCostFields);
    };

    // Generate options for ETC, DJF, NJ based on imported data from respective tables
    const generateFieldOptions = (fieldType) => {
        let sourceData = [];

        switch (fieldType) {
            case 'etc':
                sourceData = etcData;
                break;
            case 'djf':
                sourceData = djfData;
                break;
            case 'nj':
                sourceData = njData;
                break;
            default:
                sourceData = [];
        }

        return sourceData.map(item => ({
            value: item.id,  // Use unique ID instead of value
            label: item.label,
            originalValue: item.value  // Keep original value for reference
        }));
    };

    // Add destination
    const addDestination = () => {
        const newDestination = {
            id: Date.now(),
            access_level: "1",
            surface_area: null,
            team_name: "1",
            existing_key: "",
            buyer: null,
            object: null,
            surface_sel: null,
            consumption_target: "buyer",
            no_cost_destination: false,
            destination_order: ""
        };
        setDestinations([...destinations, newDestination]);
    };

    // Remove destination
    const removeDestination = (id) => {
        if (destinations.length > 1) {
            setDestinations(destinations.filter(dest => dest.id !== id));
        }
    };

    // Update destination
    const updateDestination = (id, field, value) => {
        setDestinations(destinations.map(dest =>
            dest.id === id ? { ...dest, [field]: value } : dest
        ));
    };

    // Object selection (Object flow + Consumption→Object): set the object AND auto-fill
    // the Surface field from the object's parent surface (object_surface), per client
    // request. Matches the imported surface whose code equals the object's object_surface.
    const handleObjectChange = (id, opt) => {
        const matchedSurface = (opt && opt.object_surface != null)
            ? surfaceData.find(s => String(s.surface).trim() === String(opt.object_surface).trim())
            : null;
        setDestinations(prev => prev.map(dest =>
            dest.id === id
                ? { ...dest, object: opt, ...(matchedSurface ? { surface_sel: matchedSurface } : {}) }
                : dest
        ));
    };

    // Reconstruct formula history from existing formula
    const reconstructFormulaHistory = (formula) => {
        const history = [""];

        // Split formula into tokens, preserving spaces and operators
        const tokens = formula.trim().split(/(\s+)/).filter(token => token.trim());

        let currentFormula = "";

        for (let i = 0; i < tokens.length; i++) {
            const token = tokens[i].trim();
            if (token) {
                // Add space before token if formula is not empty and doesn't end with space
                if (currentFormula && !currentFormula.endsWith(" ")) {
                    currentFormula += " ";
                }
                currentFormula += token;

                // Add space after token for readability
                if (!currentFormula.endsWith(" ")) {
                    currentFormula += " ";
                }

                // Add this step to history
                history.push(currentFormula);
            }
        }

        return history;
    };

    // Formula history management
    const addToHistory = (formula, setFieldValue) => {
        const newHistory = formulaHistory.slice(0, historyIndex + 1);
        newHistory.push(formula);
        setFormulaHistory(newHistory);
        setHistoryIndex(newHistory.length - 1);
        setFieldValue("formula", formula);
    };

    // Undo function
    const undoFormula = (setFieldValue) => {
        if (historyIndex > 0) {
            const newIndex = historyIndex - 1;
            setHistoryIndex(newIndex);
            setFieldValue("formula", formulaHistory[newIndex]);
        }
    };

    // Redo function
    const redoFormula = (setFieldValue) => {
        if (historyIndex < formulaHistory.length - 1) {
            const newIndex = historyIndex + 1;
            setHistoryIndex(newIndex);
            setFieldValue("formula", formulaHistory[newIndex]);
        }
    };

    const handleSubmit = async (values) => {
        try {
            // A destination is "object-routed" if it's an Object destination or a
            // Consumption routed to an Object. Used for the single-routing-type guard.
            const isObjectRouted = (dest) =>
                dest.access_level === "3" ||
                (dest.access_level === "2" && dest.consumption_target === "object");

            // Validate destinations have their required primary selection.
            const hasPrimary = (dest) => {
                if (dest.access_level === "3") {
                    return dest.object?.value != null && dest.surface_sel?.value != null;
                }
                if (dest.access_level === "2" && dest.consumption_target === "object") {
                    return dest.surface_area?.value != null && dest.object?.value != null && dest.surface_sel?.value != null;
                }
                return dest.surface_area?.value != null;
            };
            const validDestinations = destinations.filter(hasPrimary);
            if (validDestinations.length === 0) {
                alert("Please complete at least one destination. Object flow needs an Object + Surface; Consumption→Object needs Consumption + Surface + Object.");
                return;
            }

            // Guard: a key routes via Surface OR via Object, never both on the same key.
            const routedFlags = validDestinations.map(isObjectRouted);
            if (routedFlags.some(Boolean) && routedFlags.some(f => !f)) {
                alert("A key cannot mix Surface-routed and Object-routed destinations. Use one type per key.");
                return;
            }

            const resolveCostId = (cost) => {
                if (!cost) return null;

                const directId = cost.id ?? cost.cost_id ?? cost.costId;
                if (directId && !Number.isNaN(parseInt(directId))) {
                    return parseInt(directId);
                }

                const numericValue = parseFloat(cost.value);
                if (!Number.isNaN(numericValue)) {
                    const matchingCost = costData.find(option => {
                        const optionValue = parseFloat(option.value);
                        const optionCostValue = parseFloat(option.cost_value ?? option.amount ?? option.original_value);
                        return (!Number.isNaN(optionValue) && optionValue === numericValue) ||
                            (!Number.isNaN(optionCostValue) && optionCostValue === numericValue);
                    });

                    if (matchingCost?.id && !Number.isNaN(parseInt(matchingCost.id))) {
                        return parseInt(matchingCost.id);
                    }

                    return numericValue;
                }

                return null;
            };

            const resolvedCosts = (values.costs || [])
                .map(resolveCostId)
                .filter(costId => costId !== null);

            // Validate destination_order values
            for (const dest of destinations) {
                const rawOrder = dest.destination_order;
                if (rawOrder !== "" && rawOrder !== null && rawOrder !== undefined) {
                    const parsed = parseInt(rawOrder, 10);
                    if (!Number.isInteger(parsed) || parsed < 1) {
                        alert("Destination order must be a positive integer (or empty to clear).");
                        return;
                    }
                }
            }

            // Map destinations across the three flows: Surface, Consumption (→Buyer or →Object), Object.
            const mappedDestinations = destinations.map(dest => {
                const isObject = dest.access_level === "3";
                const isConsumption = dest.access_level === "2";
                const consObject = isConsumption && dest.consumption_target === "object";
                const toInt = (v) => (v !== null && v !== undefined && v !== "" ? parseInt(v, 10) : null);

                // destination_id (calc basis): Object flow → the selected Surface; else the
                // primary surface/consumption selection.
                const destinationId = isObject
                    ? (dest.surface_sel?.value != null ? parseFloat(dest.surface_sel.value) : null)
                    : (dest.surface_area?.value != null ? parseFloat(dest.surface_area.value) : null);

                const rawOrder = dest.destination_order;
                const destinationOrder =
                    rawOrder === "" || rawOrder === null || rawOrder === undefined
                        ? null
                        : parseInt(rawOrder, 10);

                // object_id: set for Object flow and Consumption→Object.
                const objectId = (isObject || consObject) ? toInt(dest.object?.value) : null;
                // surface_id: the Surface field for Object flow + Consumption→Object.
                const surfaceId = (isObject || consObject) ? toInt(dest.surface_sel?.value) : null;
                // buyer_id: Surface flow, or Consumption→Buyer (NOT object-routed flows).
                const buyerId = (!isObject && !consObject && dest.buyer?.value) ? parseInt(dest.buyer.value) : null;
                // Key name only applies to Surface and Consumption→Buyer.
                const keyName = (isObject || consObject) ? "" : (dest.existing_key?.trim() || "");

                return {
                    destination_type: isObject ? "object" : (isConsumption ? "consumption" : "surface"),
                    destination_id: destinationId,
                    destination_key_name: keyName,
                    ponderation: parseFloat(dest.team_name) || 1,
                    buyer_id: buyerId,
                    object_id: objectId,
                    surface_id: surfaceId,
                    no_cost_destination: dest.no_cost_destination || false,
                    destination_order: destinationOrder
                };
            }).filter(dest => dest.destination_id !== null && !isNaN(dest.destination_id)); // Only include destinations with valid numeric IDs

            if (mappedDestinations.length === 0) {
                alert("Please select at least one valid destination with a valid ID");
                return;
            }

            const payload = {
                building_id: parseInt(values.property_type.value),
                building_time_period_id: parseInt(values.period.value),
                key_order: values.key_order !== "" && values.key_order !== null && values.key_order !== undefined
                    ? parseInt(values.key_order)
                    : null,
                key_name: values.key_name.trim(),
                formula: values.formula.trim(),
                costs: resolvedCosts,
                cost_fields: values.costFields || {},
                // Allow creating/updating keys without primary costs.
                // Backend will still validate and handle scenarios with or without transferred costs.
                allow_empty_costs: resolvedCosts.length === 0,
                destinations: mappedDestinations,
                notes: values.property_address?.trim() || ""
            };

            console.log("Submitting payload:", payload);
            console.log("Destinations before mapping:", destinations);
            console.log("Mapped destinations:", mappedDestinations);

            const url = isEditing
                ? `${process.env.REACT_APP_API_URL}key/${editId}`
                : `${process.env.REACT_APP_API_URL}key/create`;

            const method = isEditing ? 'put' : 'post';

            const res = await axios[method](url, payload, {
                headers: {
                    "api-key": process.env.REACT_APP_API_KEY,
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                    "Content-Type": "application/json"
                },
            });

            if (res.data.status === "success") {
                alert(isEditing ? "Key updated successfully!" : "Key created successfully!");
                navigate(backTarget);
            } else {
                alert(`Error ${isEditing ? 'updating' : 'creating'} key: ` + res.data.message);
            }
        } catch (err) {
            console.error("Error creating key:", err);
            console.error("Error details:", {
                message: err.message,
                response: err.response?.data,
                payload: err.config?.data
            });
            const errorMessage = err.response?.data?.message || err.message || "Oops! Something went wrong. Please try again later";
            alert("Error creating key: " + errorMessage);
        }
    };

    return (
        <div className="admin-page">

            <div className="back-title d-flex align-items-center">
                <button
                    className="back-btn-title"
                    type="button"
                    onClick={() => navigate(backTarget)}
                >
                    <i className="icon-icon-11"></i>
                </button>
                <h3 className="page-section-title">{isEditing ? 'Edit Key' : isDuplicating ? 'Duplicate Key' : 'Create Key'}</h3>
            </div>
            <div className="create-key-form-box">
                <Formik
                    innerRef={formikRef}
                    enableReinitialize
                    initialValues={{
                        property_type: editData ? { value: editData.building_id, label: editData.building_name } : null,
                        period: editData && editData.start_date && editData.end_date ? {
                            value: editData.building_time_period_id,
                            label: `${new Date(editData.start_date).toLocaleDateString()} - ${new Date(editData.end_date).toLocaleDateString()}`
                        } : null,
                        costs: (editData && editData.costs && costData.length > 0 && !isDuplicating) ? editData.costs.map(costId => {
                            // Find the matching cost from costData by ID (only for edit mode, not duplicate)
                            const costOption = costData.find(c => c.value === costId || parseInt(c.value) === parseInt(costId));
                            return costOption || null;
                        }).filter(Boolean) : [],
                        costFields: editData?.cost_fields_data || {},
                        formula: editData?.formula || "",
                        key_name: (editData?.key_name && !isDuplicating) ? editData.key_name : "",
                        key_order: !isDuplicating && editData?.key_order ? String(editData.key_order) : "",
                        access_level: "1",
                        surface_area: null,
                        team_name: "1",
                        existing_key: "",
                        buyer_allocated: false,
                        property_address: editData?.notes || ""
                    }}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ values, setFieldValue }) => {
                        const handleInsert = (value) => {
                            const currentFormula = values.formula || "";
                            const newFormula = (currentFormula.trim() ? currentFormula + " " : "") + value + " ";
                            addToHistory(newFormula, setFieldValue);
                        };

                        const applyPresetFormula = (formula) => {
                            if (!formula) return;
                            addToHistory(formula, setFieldValue);
                        };

                        return (
                            <Form>
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label htmlFor="keyName">Building Name</label>
                                            <div className="custom-select">
                                                <Select
                                                    options={buildings}
                                                    value={values.property_type}
                                                    onChange={(opt) => handleBuildingChange(opt, setFieldValue)}
                                                    placeholder="Enter building name"
                                                />
                                            </div>
                                            <ErrorMessage name="property_type" component="div" className="text-danger mt-1" />
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label htmlFor="keyValue">Select Period</label>
                                            <div className="custom-select">
                                                <Select
                                                    options={periods}
                                                    value={values.period}
                                                    onChange={(opt) => handlePeriodChange(opt, values.property_type, setFieldValue)}
                                                    placeholder="Select period"
                                                    isDisabled={!values.property_type}
                                                />
                                            </div>
                                            <ErrorMessage name="period" component="div" className="text-danger mt-1" />
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label htmlFor="costs">Costs</label>
                                            <div className="custom-select multi-custom-select">
                                                <Select
                                                    isMulti
                                                    options={costData}
                                                    value={values.costs}
                                                    onChange={(opts) => handleCostChange(opts, setFieldValue, values.costFields)}
                                                    placeholder="Select value"
                                                    classNamePrefix="costs-select"
                                                    isDisabled={!values.period}
                                                    isLoading={loading}
                                                />
                                            </div>
                                            {values.costs && values.costs.length > 0 && (
                                                <ul className="mt-3 d-flex gap-3 flex-wrap multi-custom-option">
                                                    {values.costs.map((cost, idx) => (
                                                        <li key={cost.value} className="d-flex align-items-center">
                                                            {cost.label}
                                                            <span
                                                                onClick={() =>
                                                                    setFieldValue(
                                                                        "costs",
                                                                        values.costs.filter((_, i) => i !== idx)
                                                                    )
                                                                }
                                                            >
                                                                <i className="icon-icon-14 d-flex"></i>
                                                            </span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                            {transferredCosts.length > 0 && (
                                                <div className="mt-3">
                                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                                        <label className="form-label mb-0 fw-semibold">Transferred Costs</label>
                                                        <small className="text-muted">Amounts read-only · pick one factor (ETC/DJF/NJ) per cost</small>
                                                    </div>
                                                    <div className="border rounded" style={{ overflow: 'hidden' }}>
                                                        <table className="table table-sm mb-0" style={{ fontSize: '13px' }}>
                                                            <thead style={{ background: '#f8f9fa' }}>
                                                                <tr>
                                                                    <th style={{ padding: '6px 12px', fontWeight: 600 }}>Cost</th>
                                                                    <th style={{ padding: '6px 12px', fontWeight: 600 }}>From</th>
                                                                    <th style={{ padding: '6px 12px', fontWeight: 600, textAlign: 'right' }}>Amount</th>
                                                                    <th style={{ padding: '6px 8px', fontWeight: 600, textAlign: 'center', width: '60px' }}>ETC</th>
                                                                    <th style={{ padding: '6px 8px', fontWeight: 600, textAlign: 'center', width: '60px' }}>DJF</th>
                                                                    <th style={{ padding: '6px 8px', fontWeight: 600, textAlign: 'center', width: '60px' }}>NJ</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {transferredCosts.map((cost, idx) => {
                                                                    const amount = cost.amountWithoutVat ?? cost.amountWithVat ?? 0;
                                                                    const cid = cost.costId ? String(cost.costId) : null;
                                                                    const cur = cid ? (values.costFields?.[cid] || {}) : {};
                                                                    const isETC = cur.etc?.length > 0;
                                                                    const isDJF = cur.djf?.length > 0;
                                                                    const isNJ  = cur.nj?.length > 0;
                                                                    const setFactor = (type) => {
                                                                        if (!cid) return;
                                                                        const opts = type ? generateFieldOptions(type) : [];
                                                                        const newCostFields = {
                                                                            ...values.costFields,
                                                                            [cid]: {
                                                                                etc: type === 'etc' ? opts : [],
                                                                                djf: type === 'djf' ? opts : [],
                                                                                nj:  type === 'nj'  ? opts : []
                                                                            }
                                                                        };
                                                                        setCostFields(newCostFields);
                                                                        setFieldValue("costFields", newCostFields);
                                                                    };
                                                                    const cellTd = { padding: '6px 8px', textAlign: 'center' };
                                                                    return (
                                                                        <tr key={cost.id || `transfer-${idx}`}>
                                                                            <td style={{ padding: '6px 12px' }}>
                                                                                <span className="fw-semibold">{cost.costName || `Transferred Cost ${idx + 1}`}</span>
                                                                            </td>
                                                                            <td style={{ padding: '6px 12px' }} className="text-muted">{cost.sourceKey || '-'}</td>
                                                                            <td style={{ padding: '6px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                                                {amount.toLocaleString(undefined, { maximumFractionDigits: 2 })} {cost.unit || 'CHF'}
                                                                            </td>
                                                                            <td style={cellTd}>
                                                                                <input
                                                                                    type="checkbox"
                                                                                    checked={isETC}
                                                                                    onChange={(e) => setFactor(e.target.checked ? 'etc' : null)}
                                                                                    disabled={!cid || etcData.length === 0}
                                                                                    style={{ cursor: cid && etcData.length > 0 ? 'pointer' : 'not-allowed' }}
                                                                                />
                                                                            </td>
                                                                            <td style={cellTd}>
                                                                                <input
                                                                                    type="checkbox"
                                                                                    checked={isDJF}
                                                                                    onChange={(e) => setFactor(e.target.checked ? 'djf' : null)}
                                                                                    disabled={!cid || djfData.length === 0}
                                                                                    style={{ cursor: cid && djfData.length > 0 ? 'pointer' : 'not-allowed' }}
                                                                                />
                                                                            </td>
                                                                            <td style={cellTd}>
                                                                                <input
                                                                                    type="checkbox"
                                                                                    checked={isNJ}
                                                                                    onChange={(e) => setFactor(e.target.checked ? 'nj' : null)}
                                                                                    disabled={!cid || njData.length === 0}
                                                                                    style={{ cursor: cid && njData.length > 0 ? 'pointer' : 'not-allowed' }}
                                                                                />
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </table>
                                                    </div>

                                                    <small className="text-muted d-block mt-2">
                                                        Primary costs are optional because transferred costs exist.
                                                    </small>
                                                </div>
                                            )}
                                            <ErrorMessage name="costs" component="div" className="text-danger mt-1" />
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label htmlFor="keyName">Key Name</label>
                                            <Field
                                                name="key_name"
                                                className="form-control"
                                                placeholder="Enter key name"
                                            />
                                            <ErrorMessage name="key_name" component="div" className="text-danger mt-1" />
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label htmlFor="key_order">Key Order</label>
                                            <Field
                                                name="key_order"
                                                type="number"
                                                min="1"
                                                className="form-control"
                                                placeholder="Enter key order (e.g. 1, 2, 3)"
                                            />
                                            <small className="text-muted">
                                                Lower numbers appear first in Keys and Reports.
                                            </small>
                                            <ErrorMessage name="key_order" component="div" className="text-danger mt-1" />
                                        </div>
                                    </div>

                                    {/* Dynamic Cost Fields */}
                                    {values.costs && values.costs.length > 0 && (
                                        <div className="col-12">
                                            <div className="cost-fields-section">
                                                <h6 className="mb-3">Additional Cost Fields</h6>
                                                {values.costs.map((cost, costIndex) => (
                                                    <div key={cost.value} className="cost-field-group mb-4 p-3 border rounded">
                                                        <h6 className="mb-3 text-primary">{cost.label}</h6>
                                                        <div className="row">
                                                            <div className="col-md-4">
                                                                <div className="form-group">
                                                                    <label className="d-flex align-items-center">
                                                                        <input
                                                                            type="checkbox"
                                                                            className="me-2"
                                                                            checked={values.costFields?.[cost.value]?.etc?.length > 0 || false}
                                                                            onChange={(e) => {
                                                                                const newCostFields = {
                                                                                    ...values.costFields,
                                                                                    [cost.value]: {
                                                                                        ...values.costFields?.[cost.value],
                                                                                        etc: e.target.checked ? generateFieldOptions('etc') : []
                                                                                    }
                                                                                };
                                                                                setCostFields(newCostFields);
                                                                                setFieldValue("costFields", newCostFields);
                                                                            }}
                                                                            disabled={etcData.length === 0}
                                                                        />
                                                                        ETC
                                                                    </label>
                                                                </div>
                                                            </div>
                                                            <div className="col-md-4">
                                                                <div className="form-group">
                                                                    <label className="d-flex align-items-center">
                                                                        <input
                                                                            type="checkbox"
                                                                            className="me-2"
                                                                            checked={values.costFields?.[cost.value]?.djf?.length > 0 || false}
                                                                            onChange={(e) => {
                                                                                const newCostFields = {
                                                                                    ...values.costFields,
                                                                                    [cost.value]: {
                                                                                        ...values.costFields?.[cost.value],
                                                                                        djf: e.target.checked ? generateFieldOptions('djf') : []
                                                                                    }
                                                                                };
                                                                                setCostFields(newCostFields);
                                                                                setFieldValue("costFields", newCostFields);
                                                                            }}
                                                                            disabled={djfData.length === 0}
                                                                        />
                                                                        DJF
                                                                    </label>
                                                                </div>
                                                            </div>
                                                            <div className="col-md-4">
                                                                <div className="form-group">
                                                                    <label className="d-flex align-items-center">
                                                                        <input
                                                                            type="checkbox"
                                                                            className="me-2"
                                                                            checked={values.costFields?.[cost.value]?.nj?.length > 0 || false}
                                                                            onChange={(e) => {
                                                                                const newCostFields = {
                                                                                    ...values.costFields,
                                                                                    [cost.value]: {
                                                                                        ...values.costFields?.[cost.value],
                                                                                        nj: e.target.checked ? generateFieldOptions('nj') : []
                                                                                    }
                                                                                };
                                                                                setCostFields(newCostFields);
                                                                                setFieldValue("costFields", newCostFields);
                                                                            }}
                                                                            disabled={njData.length === 0}
                                                                        />
                                                                        NJ
                                                                    </label>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                                <ErrorMessage name="costFields" component="div" className="text-danger mt-1" />
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Formula Section */}
                                <div className="key-card mb-4 p-lg-4 p-3">
                                    <div className="form-group">
                                        <div className="d-flex align-items-center justify-content-between gap-2 calculation-label">
                                            <label className="mb-0">Formula/Calculation Method</label>
                                            <div className="d-flex flex-wrap align-items-center gap-2">
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-primary"
                                                    onClick={() => applyPresetFormula(presetFormulas.consumption)}
                                                >
                                                    Consumption Key
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-primary"
                                                    onClick={() => applyPresetFormula(presetFormulas.surface)}
                                                >
                                                    Surface Key
                                                </button>
                                            </div>
                                            <div className="button-group d-flex align-items-center gap-2">
                                                <button
                                                    type="button"
                                                    className="arrow-btn"
                                                    onClick={() => undoFormula(setFieldValue)}
                                                    disabled={historyIndex <= 0}
                                                    title={`Undo (${historyIndex}/${formulaHistory.length - 1})`}
                                                    style={{ opacity: historyIndex <= 0 ? 0.5 : 1 }}
                                                >
                                                    <i className="icon-icon-15"></i>
                                                </button>
                                                <button
                                                    type="button"
                                                    className="arrow-btn"
                                                    onClick={() => redoFormula(setFieldValue)}
                                                    disabled={historyIndex >= formulaHistory.length - 1}
                                                    title={`Redo (${historyIndex}/${formulaHistory.length - 1})`}
                                                    style={{ opacity: historyIndex >= formulaHistory.length - 1 ? 0.5 : 1 }}
                                                >
                                                    <i className="icon-icon-16"></i>
                                                </button>
                                            </div>
                                        </div>
                                        <Field
                                            as="textarea"
                                            name="formula"
                                            className="form-control"
                                            placeholder="Use variables and operators below to build your formula"
                                            rows="3"
                                            readOnly
                                        />
                                        <ErrorMessage name="formula" component="div" className="text-danger mt-1" />
                                    </div>

                                    <div className="form-group">
                                        <label>Variables</label>
                                        <div className="button-group d-flex align-items-center gap-3 flex-wrap">
                                            {variables.map((variable) => (
                                                <button
                                                    type="button"
                                                    key={variable}
                                                    onClick={() => handleInsert(variable)}
                                                    className="btn variable-btn"
                                                >
                                                    {variable}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="form-group mb-0">
                                        <label>Operators</label>
                                        <div className="button-group d-flex align-items-center gap-3 flex-wrap">
                                            {operators.map((op) => (
                                                <button
                                                    type="button"
                                                    key={op}
                                                    onClick={() => handleInsert(op)}
                                                    className="btn operator-btn"
                                                >
                                                    {op}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <div className="destination-box">
                                    <div className="destination-title d-flex align-items-center justify-content-between gap-2 flex-wrap mb-md-4 mb-3">
                                        <h6>Destination</h6>
                                        {/* <button 
                                            type="button" 
                                            className="add-more-btn"
                                            onClick={addDestination}
                                        >
                                            <i className="icon-icon-5"></i>
                                            Add More
                                        </button> */}
                                    </div>
                                    {destinations.map((destination, index) => (
                                        <div key={destination.id} className="key-card destination-key-card mb-4 px-3 py-2">
                                            <div className="d-flex align-items-center justify-content-between gap-2">
                                                <div className="d-flex gap-4 flex-wrap mb-4">
                                                    <label className="form-check-label form-radio-label d-flex align-items-center mb-0">
                                                        <input
                                                            type="radio"
                                                            name={`access_level_${destination.id}`}
                                                            checked={destination.access_level === "1"}
                                                            onChange={() => updateDestination(destination.id, "access_level", "1")}
                                                            className="form-check-input"
                                                        />
                                                        <span>Surface</span>
                                                    </label>
                                                    <label className="form-check-label form-radio-label d-flex align-items-center mb-0">
                                                        <input
                                                            type="radio"
                                                            name={`access_level_${destination.id}`}
                                                            checked={destination.access_level === "2"}
                                                            onChange={() => updateDestination(destination.id, "access_level", "2")}
                                                            className="form-check-input"
                                                        />
                                                        <span>Consumption</span>
                                                    </label>
                                                    <label className="form-check-label form-radio-label d-flex align-items-center mb-0">
                                                        <input
                                                            type="radio"
                                                            name={`access_level_${destination.id}`}
                                                            checked={destination.access_level === "3"}
                                                            onChange={() => updateDestination(destination.id, "access_level", "3")}
                                                            className="form-check-input"
                                                        />
                                                        <span>Object</span>
                                                    </label>
                                                    {destination.access_level === "2" && (
                                                        <div className="d-flex align-items-center gap-3">
                                                            <span className="mb-0">Destination:</span>
                                                            <label className="form-check-label form-radio-label d-flex align-items-center mb-0">
                                                                <input
                                                                    type="radio"
                                                                    name={`cons_target_${destination.id}`}
                                                                    checked={(destination.consumption_target || "buyer") === "buyer"}
                                                                    onChange={() => updateDestination(destination.id, "consumption_target", "buyer")}
                                                                    className="form-check-input"
                                                                />
                                                                <span>Buyer</span>
                                                            </label>
                                                            <label className="form-check-label form-radio-label d-flex align-items-center mb-0">
                                                                <input
                                                                    type="radio"
                                                                    name={`cons_target_${destination.id}`}
                                                                    checked={destination.consumption_target === "object"}
                                                                    onChange={() => updateDestination(destination.id, "consumption_target", "object")}
                                                                    className="form-check-input"
                                                                />
                                                                <span>Object</span>
                                                            </label>
                                                        </div>
                                                    )}
                                                    <label className="form-check-label d-flex align-items-center mb-0">
                                                        <input
                                                            type="checkbox"
                                                            checked={destination.no_cost_destination || false}
                                                            onChange={(e) => updateDestination(destination.id, "no_cost_destination", e.target.checked)}
                                                            className="form-check-input"
                                                        />
                                                        <span>No Cost Destination</span>
                                                    </label>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <label className="mb-0" htmlFor={`destination_order_${destination.id}`}>Order</label>
                                                        <input
                                                            id={`destination_order_${destination.id}`}
                                                            type="number"
                                                            min="1"
                                                            step="1"
                                                            className="form-control"
                                                            style={{ width: 100 }}
                                                            value={destination.destination_order ?? ""}
                                                            onChange={(e) => updateDestination(destination.id, "destination_order", e.target.value)}
                                                            placeholder="Order"
                                                        />
                                                    </div>
                                                </div>
                                                {destinations.length > 1 && (
                                                    <div className="d-flex justify-content-end">
                                                        <button
                                                            type="button"
                                                            className="btn btn-danger btn-sm"
                                                            onClick={() => removeDestination(destination.id)}
                                                        >
                                                            Remove
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                            {(() => {
                                                const isObject = destination.access_level === "3";
                                                const isConsumption = destination.access_level === "2";
                                                const consObject = isConsumption && destination.consumption_target === "object";
                                                return (
                                            <div className="row">
                                                {/* Col 1 — primary selection (Object for object flow, else Surface/Consumption) */}
                                                <div className="col-lg-3 col-md-4 col-sm-6 col-12">
                                                    <div className="form-group mb-0">
                                                        <label>{isObject ? "Object" : (destination.access_level === "1" ? "Surface area" : "Consumption")}</label>
                                                        <div className="custom-select">
                                                            {isObject ? (
                                                                <Select
                                                                    options={objectData}
                                                                    value={destination.object}
                                                                    onChange={(opt) => handleObjectChange(destination.id, opt)}
                                                                    placeholder="Select object"
                                                                    isDisabled={!values.period}
                                                                />
                                                            ) : (
                                                                <Select
                                                                    options={destination.access_level === "1" ? surfaceData : consumptionData}
                                                                    value={destination.surface_area}
                                                                    onChange={(opt) => {
                                                                        setDestinations(prev => prev.map(dest =>
                                                                            dest.id === destination.id
                                                                                ? { ...dest, surface_area: opt, ...(destination.access_level === "1" ? { buyer: opt } : {}) }
                                                                                : dest
                                                                        ));
                                                                    }}
                                                                    placeholder="Select"
                                                                    isDisabled={!values.period}
                                                                />
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                                {/* Col 2 — Ponderation */}
                                                <div className="col-lg-3 col-md-4 col-sm-6 col-12">
                                                    <div className="form-group mb-0">
                                                        <label>Ponderation <span>(by default: 1)</span></label>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            className="form-control"
                                                            value={destination.team_name}
                                                            onChange={(e) => updateDestination(destination.id, "team_name", e.target.value)}
                                                            placeholder="1"
                                                        />
                                                    </div>
                                                </div>
                                                {/* Col 3 — Consumption→Object: Object | Object flow: Surface | else: Key.
                                                    (Client asked to put Object before Surface for Consumption→Object.) */}
                                                <div className="col-lg-3 col-md-4 col-sm-6 col-12">
                                                    <div className="form-group mb-0">
                                                        {consObject ? (
                                                            <>
                                                                <label>Object</label>
                                                                <div className="custom-select">
                                                                    <Select
                                                                        options={objectData}
                                                                        value={destination.object}
                                                                        onChange={(opt) => handleObjectChange(destination.id, opt)}
                                                                        placeholder="Select object"
                                                                        isDisabled={!values.period}
                                                                    />
                                                                </div>
                                                            </>
                                                        ) : isObject ? (
                                                            <>
                                                                <label>Surface</label>
                                                                <div className="custom-select">
                                                                    <Select
                                                                        options={surfaceData}
                                                                        value={destination.surface_sel}
                                                                        onChange={(opt) => updateDestination(destination.id, "surface_sel", opt)}
                                                                        placeholder="Select surface"
                                                                        isDisabled={!values.period}
                                                                    />
                                                                </div>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <label htmlFor="Existing">Enter Existing/New Key Name</label>
                                                                <div className="custom-select">
                                                                    <CreatableSelect
                                                                        options={existingKeys}
                                                                        value={existingKeys.find(k => k.value === destination.existing_key) ||
                                                                            (destination.existing_key ? { value: destination.existing_key, label: destination.existing_key } : null)}
                                                                        onChange={(opt) => updateDestination(destination.id, "existing_key", opt?.value || "")}
                                                                        onCreateOption={(inputValue) => updateDestination(destination.id, "existing_key", inputValue)}
                                                                        placeholder="Select or type new key name"
                                                                        isClearable
                                                                    />
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                                {/* Col 4 — Consumption→Object: Surface | else: Buyer (auto=Object for object flow) */}
                                                <div className="col-lg-3 col-md-4 col-sm-6 col-12">
                                                    <div className="form-group mb-0">
                                                        {consObject ? (
                                                            <>
                                                                <label>Surface</label>
                                                                <div className="custom-select">
                                                                    <Select
                                                                        options={surfaceData}
                                                                        value={destination.surface_sel}
                                                                        onChange={(opt) => updateDestination(destination.id, "surface_sel", opt)}
                                                                        placeholder="Select surface"
                                                                        isDisabled={!values.period}
                                                                    />
                                                                </div>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <label htmlFor="buyer">Buyer{isObject ? " (= Object)" : ""}</label>
                                                                <div className="custom-select">
                                                                    <Select
                                                                        options={destination.access_level === "1" ? surfaceData : surfaceNames}
                                                                        value={isObject ? destination.object : destination.buyer}
                                                                        onChange={(opt) => updateDestination(destination.id, "buyer", opt)}
                                                                        placeholder="Select buyer"
                                                                        isDisabled={!values.period || isObject || (isConsumption && surfaceNames.length === 0)}
                                                                        isClearable
                                                                    />
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                                );
                                            })()}
                                        </div>
                                    ))}
                                    <div className="destination-title d-flex align-items-center justify-content-between gap-2 flex-wrap mb-md-4 mb-3">
                                        <h6></h6>
                                        <button
                                            type="button"
                                            className="add-more-btn"
                                            onClick={addDestination}
                                        >
                                            <i className="icon-icon-5"></i>
                                            Add More
                                        </button>
                                    </div>
                                    <div className="form-group">
                                        <label>Notes</label>
                                        <Field
                                            as="textarea"
                                            name="property_address"
                                            className="form-control"
                                            placeholder="Add any additional notes or comments"
                                        />
                                    </div>
                                    <div className="button-group d-flex align-items-center">
                                        <button type="submit" className="btns">
                                            {isEditing ? 'Update Key' : isDuplicating ? 'Create Duplicate Key' : 'Create Key'}
                                        </button>
                                    </div>
                                </div>
                            </Form>
                        );
                    }}
                </Formik>
            </div>
        </div>
    );
};

export default CreateKey;
